/**
 * Legend Container Context
 * Manages the state for the unified legend container
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

export type LegendType = 'report' | 'gpsLost' | 'weather' | 'coverage' | 'colorBy';

interface LegendSectionState {
  expanded: boolean;
}

interface LegendContainerContextValue {
  /** Individual section collapse states */
  sectionStates: Record<LegendType, LegendSectionState>;
  toggleSection: (type: LegendType) => void;
  setSectionExpanded: (type: LegendType, expanded: boolean) => void;
}

const LegendContainerContext = createContext<LegendContainerContextValue | null>(null);

const defaultSectionStates: Record<LegendType, LegendSectionState> = {
  report: { expanded: true },
  gpsLost: { expanded: true },
  weather: { expanded: true },
  coverage: { expanded: true },
  colorBy: { expanded: true },
};

interface LegendContainerProviderProps {
  children: React.ReactNode;
}

export const LegendContainerProvider: React.FC<LegendContainerProviderProps> = ({ children }) => {
  const [sectionStates, setSectionStates] = useState<Record<LegendType, LegendSectionState>>(defaultSectionStates);

  const toggleSection = useCallback((type: LegendType) => {
    setSectionStates(prev => ({
      ...prev,
      [type]: { expanded: !prev[type].expanded },
    }));
  }, []);

  const setSectionExpanded = useCallback((type: LegendType, expanded: boolean) => {
    setSectionStates(prev => ({
      ...prev,
      [type]: { expanded },
    }));
  }, []);

  const value = useMemo(() => ({
    sectionStates,
    toggleSection,
    setSectionExpanded,
  }), [sectionStates, toggleSection, setSectionExpanded]);

  return (
    <LegendContainerContext.Provider value={value}>
      {children}
    </LegendContainerContext.Provider>
  );
};

export const useLegendContainer = () => {
  const context = useContext(LegendContainerContext);
  if (!context) {
    throw new Error('useLegendContainer must be used within a LegendContainerProvider');
  }
  return context;
};
